import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertCircle,
  BarChart3,
  Check,
  CheckCircle2,
  ClipboardList,
  Clock3,
  Cpu,
  Database,
  Edit3,
  FileSearch,
  Home,
  Plus,
  RefreshCw,
  Save,
  Search,
  Send,
  ShieldCheck,
  Trash2,
  Users,
  X,
} from "lucide-react";
import axios from "axios";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";

const SECTION_ITEMS = [
  { id: "overview", label: "Overview", icon: Home },
  { id: "users", label: "Users", icon: Users },
  { id: "projects", label: "Projects", icon: ClipboardList },
  { id: "quality", label: "AI Quality", icon: FileSearch },
  { id: "system", label: "System", icon: Cpu },
];

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatPercent = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "0.0%";
  return `${(Number(value) * 100).toFixed(1)}%`;
};

const statusBadgeClass = (status) => {
  const normalized = (status || "").toLowerCase();
  if (normalized === "success" || normalized === "applied") return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (normalized === "failed" || normalized === "rejected") return "bg-rose-100 text-rose-700 border-rose-200";
  if (normalized === "approved") return "bg-cyan-100 text-cyan-700 border-cyan-200";
  if (normalized === "in_review") return "bg-amber-100 text-amber-700 border-amber-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
};

const queueStatusOptions = [
  { value: "pending", label: "Pending" },
  { value: "in_review", label: "In Review" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const AdminDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const [activeSection, setActiveSection] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  const [usersPayload, setUsersPayload] = useState({ users: [], total_users: 0, total_analyses: 0, total_sessions: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [projectsPayload, setProjectsPayload] = useState({ projects: [], total_projects: 0, page: 1, limit: 20 });
  const [qualityMetrics, setQualityMetrics] = useState(null);
  const [systemOverview, setSystemOverview] = useState(null);
  const [roomQueuePayload, setRoomQueuePayload] = useState({ count: 0, items: [] });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const [reviewProject, setReviewProject] = useState(null);
  const [editableRooms, setEditableRooms] = useState([]);
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewPriority, setReviewPriority] = useState("medium");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [queueActionLoadingId, setQueueActionLoadingId] = useState("");

  const fetchAdminData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [usersRes, analyticsRes, projectsRes, qualityRes, systemRes, queueRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics`, { withCredentials: true }),
        axios.get(`${API}/admin/projects?limit=40&page=1`, { withCredentials: true }),
        axios.get(`${API}/admin/detection-metrics`, { withCredentials: true }),
        axios.get(`${API}/admin/system-overview`, { withCredentials: true }),
        axios.get(`${API}/admin/room-corrections?limit=40`, { withCredentials: true }),
      ]);

      setUsersPayload(usersRes.data || { users: [] });
      setAnalytics(analyticsRes.data || null);
      setProjectsPayload(projectsRes.data || { projects: [] });
      setQualityMetrics(qualityRes.data || null);
      setSystemOverview(systemRes.data || null);
      setRoomQueuePayload(queueRes.data || { count: 0, items: [] });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403) {
        setError("Admin access denied for this account. Add your email in backend ADMIN_EMAILS to allow access.");
      } else if (err?.response?.status === 401) {
        setError("Please login to access the admin panel.");
      } else {
        setError(typeof detail === "string" ? detail : "Failed to load admin data.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReviewProject = useCallback(async (projectId) => {
    setReviewLoading(true);
    setReviewError("");
    try {
      const response = await axios.get(`${API}/admin/projects/${projectId}/review`, { withCredentials: true });
      const payload = response.data?.project || null;
      setReviewProject(payload);
      setEditableRooms((payload?.rooms || []).map((room, index) => ({
        id: room.id ?? index + 1,
        type: room.type || "Unknown",
        x: toNumber(room.x, 0),
        y: toNumber(room.y, 0),
        width: toNumber(room.width, 10),
        height: toNumber(room.height, 10),
        shape: room.shape || "rectangle",
        confidence: toNumber(room.confidence, 0.85),
        vastu_zone: room.vastu_zone || "CENTER",
        polygon: Array.isArray(room.polygon) ? room.polygon : undefined,
      })));
      setReviewNotes("");
      setReviewPriority("medium");
      setDrawerOpen(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setReviewError(typeof detail === "string" ? detail : "Could not load project review.");
    } finally {
      setReviewLoading(false);
    }
  }, []);

  const updateQueueItem = useCallback(async (correctionId, updatePayload) => {
    setQueueActionLoadingId(correctionId);
    try {
      await axios.patch(`${API}/admin/room-corrections/${correctionId}`, updatePayload, { withCredentials: true });
      const queueRes = await axios.get(`${API}/admin/room-corrections?limit=40`, { withCredentials: true });
      setRoomQueuePayload(queueRes.data || { count: 0, items: [] });
      const projectsRes = await axios.get(`${API}/admin/projects?limit=40&page=1`, { withCredentials: true });
      setProjectsPayload(projectsRes.data || { projects: [] });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      alert(typeof detail === "string" ? detail : "Queue update failed");
    } finally {
      setQueueActionLoadingId("");
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user) {
      fetchAdminData();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [authLoading, user, fetchAdminData]);

  const filteredUsers = useMemo(() => {
    const source = usersPayload?.users || [];
    const token = query.trim().toLowerCase();
    if (!token) return source;
    return source.filter((member) => {
      const name = (member.name || "").toLowerCase();
      const email = (member.email || "").toLowerCase();
      const city = (member.city || "").toLowerCase();
      const phone = (member.phone || "").toLowerCase();
      return name.includes(token) || email.includes(token) || city.includes(token) || phone.includes(token);
    });
  }, [query, usersPayload]);

  const statCards = [
    {
      label: "Users",
      value: usersPayload?.total_users ?? 0,
      note: analytics ? `+${analytics?.recent_activity?.new_users_7d || 0} in 7 days` : "Total registered",
      icon: Users,
      tone: "from-blue-500 to-indigo-500",
    },
    {
      label: "Analyses",
      value: usersPayload?.total_analyses ?? 0,
      note: analytics ? `+${analytics?.recent_activity?.new_analyses_7d || 0} in 7 days` : "Total generated",
      icon: BarChart3,
      tone: "from-orange-500 to-amber-500",
    },
    {
      label: "Detection Success",
      value: formatPercent(qualityMetrics?.detection_success_rate || 0),
      note: `${qualityMetrics?.detection_failed_count || 0} failed cases`,
      icon: CheckCircle2,
      tone: "from-emerald-500 to-green-500",
    },
    {
      label: "Correction Queue",
      value: roomQueuePayload?.count ?? 0,
      note: "Manual review tasks",
      icon: Edit3,
      tone: "from-fuchsia-500 to-pink-500",
    },
  ];

  const onRoomFieldChange = (index, field, value) => {
    setEditableRooms((prev) =>
      prev.map((room, i) => {
        if (i !== index) return room;
        if (["x", "y", "width", "height", "confidence"].includes(field)) {
          return { ...room, [field]: toNumber(value, room[field] || 0), polygon: undefined, shape: "rectangle" };
        }
        return { ...room, [field]: value };
      }),
    );
  };

  const addEditableRoom = () => {
    setEditableRooms((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        type: "Unknown Room",
        x: 10,
        y: 10,
        width: 20,
        height: 20,
        shape: "rectangle",
        confidence: 0.8,
        vastu_zone: "CENTER",
      },
    ]);
  };

  const removeEditableRoom = (index) => {
    setEditableRooms((prev) => prev.filter((_, i) => i !== index).map((room, idx) => ({ ...room, id: idx + 1 })));
  };

  const submitCorrectionQueue = async () => {
    if (!selectedProjectId || editableRooms.length === 0) return;
    setSubmitLoading(true);
    try {
      const payload = {
        corrected_rooms: editableRooms.map((room, idx) => ({
          ...room,
          id: idx + 1,
          x: Math.max(0, Math.min(100, toNumber(room.x, 0))),
          y: Math.max(0, Math.min(100, toNumber(room.y, 0))),
          width: Math.max(0, Math.min(100, toNumber(room.width, 0))),
          height: Math.max(0, Math.min(100, toNumber(room.height, 0))),
          confidence: Math.max(0, Math.min(1, toNumber(room.confidence, 0.8))),
        })),
        reviewer_notes: reviewNotes,
        priority: reviewPriority,
      };
      await axios.post(`${API}/admin/projects/${selectedProjectId}/room-corrections`, payload, { withCredentials: true });

      const [queueRes, projectsRes] = await Promise.all([
        axios.get(`${API}/admin/room-corrections?limit=40`, { withCredentials: true }),
        axios.get(`${API}/admin/projects?limit=40&page=1`, { withCredentials: true }),
      ]);
      setRoomQueuePayload(queueRes.data || { count: 0, items: [] });
      setProjectsPayload(projectsRes.data || { projects: [] });
      setDrawerOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      alert(typeof detail === "string" ? detail : "Failed to submit correction task.");
    } finally {
      setSubmitLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="admin-shell">
        <div className="admin-loading">
          <div className="admin-spinner" />
          <p>Loading admin workspace...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="admin-shell">
        <div className="admin-empty">
          <AlertCircle className="w-10 h-10 text-rose-500" />
          <h2 className="text-xl font-semibold text-slate-800 mt-3">Authentication required</h2>
          <p className="text-slate-600 mt-1">Please login to continue.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-shell">
        <div className="admin-empty">
          <ShieldCheck className="w-10 h-10 text-amber-500" />
          <h2 className="text-xl font-semibold text-slate-800 mt-3">Cannot open admin panel</h2>
          <p className="text-slate-600 mt-1 text-center max-w-xl">{error}</p>
          <button type="button" onClick={fetchAdminData} className="btn-secondary mt-5 inline-flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <div className="admin-brand">
          <span className="admin-brand-dot" />
          <div>
            <p className="admin-brand-title">Vastuspace Admin</p>
            <p className="admin-brand-subtitle">Operations Workspace</p>
          </div>
        </div>

        <nav className="admin-nav">
          {SECTION_ITEMS.map((item) => {
            const Icon = item.icon;
            const selected = activeSection === item.id;
            return (
              <button
                key={item.id}
                type="button"
                className={`admin-nav-item ${selected ? "active" : ""}`}
                onClick={() => setActiveSection(item.id)}
              >
                <Icon className="w-4 h-4" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="admin-sidebar-footer">
          <p className="text-xs text-slate-500">Signed in as</p>
          <p className="text-sm font-medium text-slate-700">{user?.name || "Admin"}</p>
          <p className="text-xs text-slate-500 truncate">{user?.email || "No email"}</p>
        </div>
      </aside>

      <main className="admin-main">
        <header className="admin-main-header">
          <div>
            <h1 className="admin-main-title">Admin Control Panel</h1>
            <p className="admin-main-subtitle">Monitor users, projects, AI quality, and platform health.</p>
          </div>
          <button type="button" onClick={fetchAdminData} className="btn-secondary inline-flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </header>

        <section className="grid md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          {statCards.map((card) => {
            const Icon = card.icon;
            return (
              <article key={card.label} className="admin-stat-card">
                <div className={`admin-stat-icon bg-gradient-to-br ${card.tone}`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <p className="admin-stat-label">{card.label}</p>
                <p className="admin-stat-value">{card.value}</p>
                <p className="admin-stat-note">{card.note}</p>
              </article>
            );
          })}
        </section>

        {activeSection === "overview" && (
          <section className="grid xl:grid-cols-3 gap-5">
            <article className="admin-panel xl:col-span-2">
              <div className="admin-panel-header">
                <h2>Recent Projects</h2>
                <span>{projectsPayload?.projects?.length || 0} shown</span>
              </div>
              <div className="overflow-x-auto">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>User</th>
                      <th>Status</th>
                      <th>Rooms</th>
                      <th>Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(projectsPayload?.projects || []).slice(0, 8).map((project) => (
                      <tr key={project.id}>
                        <td>
                          <p className="font-semibold text-slate-800">{project.property_type || "Property"}</p>
                          <p className="text-xs text-slate-500">{formatDateTime(project.created_at)}</p>
                        </td>
                        <td>
                          <p className="text-slate-700">{project.user_name || "Unknown"}</p>
                          <p className="text-xs text-slate-500">{project.user_email || "No email"}</p>
                        </td>
                        <td>
                          <span className={`admin-status-badge ${statusBadgeClass(project.detection_status)}`}>
                            {project.detection_status || "unknown"}
                          </span>
                        </td>
                        <td>{project.room_count || 0}</td>
                        <td>{project.overall_score ?? "N/A"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>

            <article className="admin-panel">
              <div className="admin-panel-header">
                <h2>System Pulse</h2>
                <span>Live</span>
              </div>
              <div className="space-y-3">
                <div className="admin-kv">
                  <span className="label">Database</span>
                  <span className="value inline-flex items-center gap-1">
                    <Database className="w-4 h-4 text-emerald-600" />
                    {systemOverview?.database || "unknown"}
                  </span>
                </div>
                <div className="admin-kv">
                  <span className="label">Analyses (24h)</span>
                  <span className="value">{systemOverview?.activity?.analyses_last_24h ?? 0}</span>
                </div>
                <div className="admin-kv">
                  <span className="label">Sessions (24h)</span>
                  <span className="value">{systemOverview?.activity?.sessions_last_24h ?? 0}</span>
                </div>
                <div className="admin-kv">
                  <span className="label">New Users (24h)</span>
                  <span className="value">{systemOverview?.activity?.new_users_last_24h ?? 0}</span>
                </div>
              </div>
            </article>
          </section>
        )}

        {activeSection === "users" && (
          <section className="admin-panel">
            <div className="admin-panel-header">
              <h2>User Directory</h2>
              <span>{filteredUsers.length} users</span>
            </div>
            <div className="admin-search-row">
              <Search className="w-4 h-4 text-slate-500" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="admin-search-input"
                placeholder="Search by name, email, city, phone"
              />
            </div>
            <div className="overflow-x-auto">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Contact</th>
                    <th>Location</th>
                    <th>Analyses</th>
                    <th>Sessions</th>
                    <th>Last Login</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((member) => (
                    <tr key={member.id}>
                      <td>
                        <p className="font-semibold text-slate-800">{member.name || "Unknown"}</p>
                        <p className="text-xs text-slate-500">Joined {formatDateTime(member.created_at)}</p>
                      </td>
                      <td>
                        <p className="text-slate-700">{member.email || "No email"}</p>
                        <p className="text-xs text-slate-500">{member.phone || "No phone"}</p>
                      </td>
                      <td>{member.city || "N/A"}</td>
                      <td>{member.analysis_count || 0}</td>
                      <td>{member.session_count || 0}</td>
                      <td>{formatDateTime(member.last_login)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {activeSection === "projects" && (
          <section className="grid xl:grid-cols-5 gap-5">
            <article className="admin-panel xl:col-span-3">
              <div className="admin-panel-header">
                <h2>Analysis Projects</h2>
                <span>{projectsPayload?.total_projects ?? 0} total</span>
              </div>
              <div className="overflow-x-auto">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Project ID</th>
                      <th>Property</th>
                      <th>Status</th>
                      <th>Rooms</th>
                      <th>Created</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(projectsPayload?.projects || []).map((project) => (
                      <tr key={project.id}>
                        <td className="font-mono text-xs">{project.id}</td>
                        <td>
                          <p className="text-slate-700">{project.property_type || "N/A"}</p>
                          <p className="text-xs text-slate-500">{project.user_name || "Unknown"}</p>
                        </td>
                        <td>
                          <span className={`admin-status-badge ${statusBadgeClass(project.detection_status)}`}>
                            {project.detection_status || "unknown"}
                          </span>
                        </td>
                        <td>{project.room_count || 0}</td>
                        <td>{formatDateTime(project.created_at)}</td>
                        <td>
                          <button
                            type="button"
                            className="admin-inline-btn"
                            onClick={() => {
                              setSelectedProjectId(project.id);
                              fetchReviewProject(project.id);
                            }}
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                            Open Review
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>

            <article className="admin-panel xl:col-span-2">
              <div className="admin-panel-header">
                <h2>Correction Queue</h2>
                <span>{roomQueuePayload?.count ?? 0} items</span>
              </div>
              <div className="admin-queue-list">
                {(roomQueuePayload?.items || []).map((item) => (
                  <div key={item.id} className="admin-queue-card">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{item.project_property_type || "Project"}</p>
                        <p className="text-xs text-slate-500 font-mono">{item.project_id}</p>
                      </div>
                      <span className={`admin-status-badge ${statusBadgeClass(item.status)}`}>{item.status}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {item.corrected_room_count || 0} corrected • {item.original_room_count || 0} original
                    </p>
                    <p className="text-xs text-slate-500">{formatDateTime(item.updated_at)}</p>
                    <div className="admin-queue-actions">
                      {queueStatusOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          disabled={queueActionLoadingId === item.id}
                          className="admin-chip-btn"
                          onClick={() => updateQueueItem(item.id, { status: option.value })}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      disabled={queueActionLoadingId === item.id}
                      className="admin-apply-btn"
                      onClick={() => updateQueueItem(item.id, { status: "approved", apply_to_project: true })}
                    >
                      {queueActionLoadingId === item.id ? (
                        <>
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          Applying...
                        </>
                      ) : (
                        <>
                          <Check className="w-3.5 h-3.5" />
                          Apply to Project
                        </>
                      )}
                    </button>
                  </div>
                ))}
                {(roomQueuePayload?.items || []).length === 0 && (
                  <p className="text-sm text-slate-500">No correction tasks yet. Open a project and submit corrections.</p>
                )}
              </div>
            </article>
          </section>
        )}

        {activeSection === "quality" && (
          <section className="grid xl:grid-cols-3 gap-5">
            <article className="admin-panel xl:col-span-2">
              <div className="admin-panel-header">
                <h2>Top Detected Room Types</h2>
                <span>Model output profile</span>
              </div>
              <div className="space-y-2">
                {(qualityMetrics?.top_room_types || []).map((roomType) => (
                  <div key={roomType.type} className="admin-room-row">
                    <span>{roomType.type}</span>
                    <span>{roomType.count}</span>
                  </div>
                ))}
                {(qualityMetrics?.top_room_types || []).length === 0 && (
                  <p className="text-sm text-slate-500">No room distribution data available yet.</p>
                )}
              </div>
            </article>

            <article className="admin-panel">
              <div className="admin-panel-header">
                <h2>Confidence Bands</h2>
                <span>Across detected rooms</span>
              </div>
              <div className="space-y-3">
                <div className="admin-kv">
                  <span className="label">High (>=0.85)</span>
                  <span className="value">{qualityMetrics?.room_confidence_bands?.high ?? 0}</span>
                </div>
                <div className="admin-kv">
                  <span className="label">Medium (0.60-0.84)</span>
                  <span className="value">{qualityMetrics?.room_confidence_bands?.medium ?? 0}</span>
                </div>
                <div className="admin-kv">
                  <span className="label">Low (&lt;0.60)</span>
                  <span className="value">{qualityMetrics?.room_confidence_bands?.low ?? 0}</span>
                </div>
                <div className="admin-kv">
                  <span className="label">Detection Success</span>
                  <span className="value">{formatPercent(qualityMetrics?.detection_success_rate || 0)}</span>
                </div>
              </div>
            </article>
          </section>
        )}

        {activeSection === "system" && (
          <section className="grid lg:grid-cols-2 gap-5">
            <article className="admin-panel">
              <div className="admin-panel-header">
                <h2>Activity Window</h2>
                <span>Last refresh: {formatDateTime(systemOverview?.timestamp)}</span>
              </div>
              <div className="space-y-3">
                <div className="admin-kv">
                  <span className="label">Analyses (24h)</span>
                  <span className="value">{systemOverview?.activity?.analyses_last_24h ?? 0}</span>
                </div>
                <div className="admin-kv">
                  <span className="label">Analyses (7d)</span>
                  <span className="value">{systemOverview?.activity?.analyses_last_7d ?? 0}</span>
                </div>
                <div className="admin-kv">
                  <span className="label">Sessions (24h)</span>
                  <span className="value">{systemOverview?.activity?.sessions_last_24h ?? 0}</span>
                </div>
                <div className="admin-kv">
                  <span className="label">New Users (24h)</span>
                  <span className="value">{systemOverview?.activity?.new_users_last_24h ?? 0}</span>
                </div>
              </div>
            </article>

            <article className="admin-panel">
              <div className="admin-panel-header">
                <h2>Ops Checklist</h2>
                <span>Recommended routine</span>
              </div>
              <div className="space-y-3 text-sm text-slate-600">
                <p className="inline-flex items-center gap-2">
                  <Clock3 className="w-4 h-4 text-indigo-500" />
                  Review failed detections and re-run critical user projects.
                </p>
                <p className="inline-flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-amber-500" />
                  Track confidence drift before publishing detection updates.
                </p>
                <p className="inline-flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-500" />
                  Confirm admin allowlist in `ADMIN_EMAILS` is current.
                </p>
              </div>
            </article>
          </section>
        )}
      </main>

      {drawerOpen && (
        <div className="admin-drawer-backdrop">
          <aside className="admin-review-drawer">
            <header className="admin-review-header">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Project Review</p>
                <h3 className="text-lg font-bold text-slate-800">{selectedProjectId}</h3>
              </div>
              <button type="button" className="admin-close-btn" onClick={() => setDrawerOpen(false)}>
                <X className="w-4 h-4" />
              </button>
            </header>

            {reviewLoading && (
              <div className="admin-review-loading">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Loading project details...
              </div>
            )}

            {reviewError && !reviewLoading && <p className="text-sm text-rose-600">{reviewError}</p>}

            {!reviewLoading && reviewProject && (
              <>
                <div className="admin-review-meta">
                  <p><strong>User:</strong> {reviewProject.user_name} ({reviewProject.user_email || "No email"})</p>
                  <p><strong>Property:</strong> {reviewProject.property_type} • {reviewProject.orientation}</p>
                  <p><strong>Detected Rooms:</strong> {editableRooms.length}</p>
                </div>

                <div className="admin-review-actions">
                  <button type="button" className="admin-inline-btn" onClick={addEditableRoom}>
                    <Plus className="w-3.5 h-3.5" />
                    Add Room
                  </button>
                </div>

                <div className="admin-review-room-list">
                  {editableRooms.map((room, index) => (
                    <div key={`${room.id}-${index}`} className="admin-room-edit-card">
                      <div className="admin-room-edit-top">
                        <p className="font-semibold text-slate-700">Room #{index + 1}</p>
                        <button type="button" className="admin-icon-btn" onClick={() => removeEditableRoom(index)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="admin-room-grid">
                        <input value={room.type || ""} onChange={(e) => onRoomFieldChange(index, "type", e.target.value)} placeholder="Type" />
                        <input value={room.vastu_zone || ""} onChange={(e) => onRoomFieldChange(index, "vastu_zone", e.target.value)} placeholder="Zone" />
                        <input type="number" step="0.1" value={room.x ?? 0} onChange={(e) => onRoomFieldChange(index, "x", e.target.value)} placeholder="x" />
                        <input type="number" step="0.1" value={room.y ?? 0} onChange={(e) => onRoomFieldChange(index, "y", e.target.value)} placeholder="y" />
                        <input type="number" step="0.1" value={room.width ?? 0} onChange={(e) => onRoomFieldChange(index, "width", e.target.value)} placeholder="width" />
                        <input type="number" step="0.1" value={room.height ?? 0} onChange={(e) => onRoomFieldChange(index, "height", e.target.value)} placeholder="height" />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="admin-review-form">
                  <label htmlFor="priority">Priority</label>
                  <select id="priority" value={reviewPriority} onChange={(e) => setReviewPriority(e.target.value)}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>

                  <label htmlFor="notes">Reviewer Notes</label>
                  <textarea
                    id="notes"
                    rows={3}
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="Explain why these room corrections are needed"
                  />
                </div>

                <div className="admin-review-footer">
                  <button type="button" className="btn-secondary" onClick={() => setDrawerOpen(false)}>
                    Cancel
                  </button>
                  <button type="button" disabled={submitLoading} className="btn-primary inline-flex items-center gap-2" onClick={submitCorrectionQueue}>
                    {submitLoading ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Submit to Queue
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </aside>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
